import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  canManageClients,
  JURISDICTIONS,
  type AutomationOutput,
  type AutomationRunOutput,
  type CreateAutomationInput,
  type UpdateAutomationInput,
  type ComplianceItemOutput,
  type UserOutput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { ClientSearchSelect } from '../components/ClientSearchSelect.js';
import { EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { fmtDate, fmtDateTime } from '../lib/date.js';

type ModalMode = { kind: 'new' } | { kind: 'edit'; automation: AutomationOutput } | null;

const TRIGGER_LABELS: Record<AutomationOutput['trigger'], string> = {
  ITEM_OVERDUE: 'Item overdue',
  ITEM_DUE_IN_3_DAYS: 'Item due in 3 days',
  ITEM_DUE_IN_N_DAYS: 'Item due in N days',
  ITEM_OVERDUE_BY_N_DAYS: 'Item overdue by N days',
  SCHEDULE: 'Recurring schedule',
  ITEM_COMPLETED: 'Item completed',
  ITEM_ASSIGNED: 'Item assigned',
  PROPOSAL_SIGNED: 'Proposal signed',
  PROPOSAL_DECLINED: 'Proposal declined',
  ENGAGEMENT_WON: 'Engagement won',
  INVOICE_PAID: 'Invoice paid',
  TIME_ENTRY_LOGGED_OVER_8H: 'Time entry over 8h',
};

const TEMPLATE_HINT = 'Variables: {{client.name}}, {{item.serviceType}}, {{item.jurisdiction}}, {{item.deadline}}, {{assignee.names}}, {{manager.name}}';

const RESULT_BADGE: Record<AutomationRunOutput['result'], string> = {
  SUCCESS: 'pill-success',
  ERROR: 'pill-danger',
  SKIPPED: 'pill-neutral',
};

const ACTION_LABELS: Record<AutomationOutput['action'], string> = {
  NOTIFY_ASSIGNEE: 'Notify assignee(s)',
  NOTIFY_MANAGER: 'Notify manager',
  SEND_CLIENT_EMAIL: 'Send client email',
  CREATE_INVOICE: 'Create invoice',
};

// Actions the executor currently log-and-skips — creating them is allowed,
// but the user should know they don't do anything yet.
const UNIMPLEMENTED_ACTIONS: AutomationOutput['action'][] = ['SEND_CLIENT_EMAIL', 'CREATE_INVOICE'];

export function AutomationsPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalMode>(null);
  const [deleting, setDeleting] = useState<AutomationOutput | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [testing, setTesting] = useState<AutomationOutput | null>(null);

  const { canDo } = useCapabilities();
  const canManage = canDo('automations.manage');

  const list = useQuery({
    queryKey: ['automations'],
    queryFn: async () => {
      const res = await fetch('/v1/automations?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: AutomationOutput[] } };
      return body.data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (input: CreateAutomationInput) => {
      const res = await fetch('/v1/automations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      setModal(null);
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateAutomationInput }) => {
      const res = await fetch(`/v1/automations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      setModal(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/automations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] });
      setDeleting(null);
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Automations</h1>
          <p className="text-sm text-muted">{list.data?.length ?? '—'} rules · time-based triggers run every 15 minutes</p>
        </div>
        {canManage && (
          <button onClick={() => setModal({ kind: 'new' })} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New automation</button>
        )}
      </header>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Then</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Fired</th>
              <th className="px-4 py-3 font-medium">Last fired</th>
              {canManage && <th className="px-4 py-3 font-medium text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted">Loading…</td></tr>}
            {list.error && <tr><td colSpan={7} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>}
            {!list.isLoading && !list.error && list.data?.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted">No automations yet.</td></tr>
            )}
            {list.data?.map((a) => (
              <Fragment key={a.id}>
              <tr className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3">
                  <div className="text-white">{a.name}</div>
                  {a.description && <div className="text-xs text-muted truncate max-w-[220px]">{a.description}</div>}
                </td>
                <td className="px-4 py-3 text-muted">
                  {TRIGGER_LABELS[a.trigger]}
                  {(a.trigger === 'ITEM_DUE_IN_N_DAYS' || a.trigger === 'ITEM_OVERDUE_BY_N_DAYS') && a.triggerParams?.days != null && (
                    <span className="text-xs"> · {a.triggerParams.days}d</span>
                  )}
                  {a.trigger === 'SCHEDULE' && a.triggerParams?.frequency && (
                    <span className="text-xs"> · {a.triggerParams.frequency} {String(a.triggerParams.hour ?? 9).padStart(2, '0')}:{String(a.triggerParams.minute ?? 0).padStart(2, '0')}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted">
                  {ACTION_LABELS[a.action]}
                  {UNIMPLEMENTED_ACTIONS.includes(a.action) && <span className="text-amber-300 text-xs"> (not yet executed)</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={a.enabled
                    ? 'px-2 py-0.5 rounded text-xs border pill-success'
                    : 'px-2 py-0.5 rounded text-xs border pill-neutral'}>
                    {a.enabled ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-white">{a.fireCount}</td>
                <td className="px-4 py-3 text-muted text-xs">{a.lastFiredAt ? fmtDateTime(a.lastFiredAt) : 'Never'}</td>
                {canManage && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                      className="text-xs text-muted hover:text-white mr-3"
                    >
                      {expanded === a.id ? 'Hide runs' : 'Runs'}
                    </button>
                    <button onClick={() => setTesting(a)} className="text-xs text-muted hover:text-white mr-3">Test</button>
                    <button
                      onClick={() => update.mutate({ id: a.id, input: { enabled: !a.enabled } })}
                      disabled={update.isPending}
                      className="text-xs text-muted hover:text-white mr-3 disabled:opacity-50"
                    >
                      {a.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <EditIcon onClick={() => setModal({ kind: 'edit', automation: a })} label="Edit automation" />
                    <DeleteIcon onClick={() => setDeleting(a)} label="Delete automation" />
                  </td>
                )}
              </tr>
              {expanded === a.id && <RunHistory automationId={a.id} colSpan={canManage ? 7 : 6} />}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <AutomationModal
          mode={modal}
          onClose={() => setModal(null)}
          onCreate={(input) => create.mutate(input)}
          onUpdate={(id, input) => update.mutate({ id, input })}
          isPending={create.isPending || update.isPending}
          error={(create.error?.message ?? update.error?.message) || null}
        />
      )}

      {deleting && (
        <ModalShell title="Delete automation" onClose={() => setDeleting(null)}>
          <p className="text-sm text-white">Delete &ldquo;{deleting.name}&rdquo;? It will stop firing immediately.</p>
          {remove.error && <p className="text-xs text-rose-400 mt-2">{remove.error.message}</p>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setDeleting(null)} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button type="button" onClick={() => remove.mutate(deleting.id)} disabled={remove.isPending} className="bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg">{remove.isPending ? 'Deleting…' : 'Delete'}</button>
          </div>
        </ModalShell>
      )}

      {testing && (
        <TestModal
          automation={testing}
          onClose={() => setTesting(null)}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ['automation-runs', testing.id] });
            queryClient.invalidateQueries({ queryKey: ['automations'] });
          }}
        />
      )}
    </div>
  );
}

function RunHistory({ automationId, colSpan }: { automationId: string; colSpan: number }) {
  const runs = useQuery({
    queryKey: ['automation-runs', automationId],
    queryFn: async () => {
      const res = await fetch(`/v1/automations/${automationId}/runs`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: AutomationRunOutput[] } };
      return body.data.items;
    },
  });

  return (
    <tr className="border-b border-border/50 bg-bg-card/40">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="text-xs font-medium text-muted mb-2">Run history (newest first, last 50)</div>
        {runs.isLoading && <div className="text-xs text-muted">Loading…</div>}
        {runs.error && <div className="text-xs text-rose-400">{(runs.error as Error).message}</div>}
        {runs.data?.length === 0 && <div className="text-xs text-muted">No runs recorded yet.</div>}
        {runs.data && runs.data.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[480px]">
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="py-1.5 pr-3 text-muted whitespace-nowrap">{fmtDateTime(r.firedAt)}</td>
                  <td className="py-1.5 pr-3">
                    <span className={`px-2 py-0.5 rounded border ${RESULT_BADGE[r.result]}`}>{r.result}</span>
                    {r.testRun && <span className="ml-1 px-1.5 py-0.5 rounded border pill-sky">test</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-muted">{r.entityLabel ?? '—'}</td>
                  <td className="py-1.5 text-muted">{r.detail ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </td>
    </tr>
  );
}

function TestModal({ automation, onClose, onDone }: { automation: AutomationOutput; onClose: () => void; onDone: () => void }) {
  const [itemId, setItemId] = useState('');
  const [outcome, setOutcome] = useState<{ fired: boolean; result: AutomationRunOutput['result']; detail: string } | null>(null);

  const items = useQuery({
    queryKey: ['compliance-items', { limit: 100 }],
    queryFn: async () => {
      const res = await fetch('/v1/compliance/items?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: ComplianceItemOutput[] } };
      return body.data.items;
    },
  });

  const test = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/automations/${automation.id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ complianceItemId: itemId }),
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { fired: boolean; result: AutomationRunOutput['result']; detail: string } };
      return body.data;
    },
    onSuccess: (data) => {
      setOutcome(data);
      onDone();
    },
  });

  return (
    <ModalShell title={`Test "${automation.name}"`} onClose={onClose}>
      <p className="text-xs text-muted mb-3">
        Executes the action once against the chosen compliance item and records a test run — the rule's fire counter is not touched.
      </p>
      <div>
        <label className="block text-xs text-muted mb-1">Compliance item</label>
        <select
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value="">— select —</option>
          {items.data?.map((i) => (
            <option key={i.id} value={i.id}>
              {i.serviceType} — {i.status}{i.deadline ? ` — due ${fmtDate(i.deadline)}` : ''}
            </option>
          ))}
        </select>
      </div>
      {test.error && <p className="text-xs text-rose-400 mt-2">{test.error.message}</p>}
      {outcome && (
        <div className="mt-3 bg-bg-card border border-border rounded-lg p-3 text-sm">
          <span className={`px-2 py-0.5 rounded border text-xs ${RESULT_BADGE[outcome.result]}`}>{outcome.result}</span>
          <span className="ml-2 text-muted text-xs">{outcome.detail}</span>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Close</button>
        <button
          type="button"
          onClick={() => test.mutate()}
          disabled={!itemId || test.isPending}
          className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
        >
          {test.isPending ? 'Running…' : 'Run test'}
        </button>
      </div>
    </ModalShell>
  );
}

function AutomationModal({
  mode,
  onClose,
  onCreate,
  onUpdate,
  isPending,
  error,
}: {
  mode: { kind: 'new' } | { kind: 'edit'; automation: AutomationOutput };
  onClose: () => void;
  onCreate: (input: CreateAutomationInput) => void;
  onUpdate: (id: string, input: UpdateAutomationInput) => void;
  isPending: boolean;
  error: string | null;
}) {
  const isEdit = mode.kind === 'edit';
  const a = isEdit ? mode.automation : null;
  const params = (a?.actionParams ?? {}) as { title?: string; body?: string; description?: string };
  const tp = a?.triggerParams ?? null;
  const [form, setForm] = useState({
    name: a?.name ?? '',
    description: a?.description ?? '',
    enabled: a?.enabled ?? true,
    trigger: a?.trigger ?? 'ITEM_OVERDUE' as AutomationOutput['trigger'],
    action: a?.action ?? 'NOTIFY_ASSIGNEE' as AutomationOutput['action'],
    condJurisdiction: a?.conditions.jurisdiction ?? '',
    condServiceType: a?.conditions.serviceType ?? '',
    condClientId: a?.conditions.clientId ?? '',
    condAssigneeId: a?.conditions.assigneeId ?? '',
    paramTitle: params.title ?? '',
    paramBody: params.body ?? '',
    paramDescription: params.description ?? '',
    tpDays: tp?.days ?? 3,
    tpFrequency: (tp?.frequency ?? 'daily') as 'daily' | 'weekly' | 'monthly',
    tpHour: tp?.hour ?? 9,
    tpMinute: tp?.minute ?? 0,
    tpDayOfWeek: tp?.dayOfWeek ?? 1,
    tpDayOfMonth: tp?.dayOfMonth ?? 1,
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  function updateForm<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const isNotify = form.action === 'NOTIFY_ASSIGNEE' || form.action === 'NOTIFY_MANAGER';
  const isDueN = form.trigger === 'ITEM_DUE_IN_N_DAYS' || form.trigger === 'ITEM_OVERDUE_BY_N_DAYS';

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const conditions: Record<string, string> = {};
    if (form.condJurisdiction) conditions.jurisdiction = form.condJurisdiction;
    if (form.condServiceType.trim()) conditions.serviceType = form.condServiceType.trim();
    if (form.condClientId) conditions.clientId = form.condClientId;
    if (form.condAssigneeId) conditions.assigneeId = form.condAssigneeId;
    const actionParams: Record<string, string> = {};
    if (isNotify) {
      if (form.paramTitle.trim()) actionParams.title = form.paramTitle.trim();
      if (form.paramBody.trim()) actionParams.body = form.paramBody.trim();
    }
    let triggerParams: Record<string, number | string> | null = null;
    if (isDueN) {
      triggerParams = { days: form.tpDays };
    } else if (form.trigger === 'SCHEDULE') {
      triggerParams = { frequency: form.tpFrequency, hour: form.tpHour, minute: form.tpMinute };
      if (form.tpFrequency === 'weekly') triggerParams.dayOfWeek = form.tpDayOfWeek;
      if (form.tpFrequency === 'monthly') triggerParams.dayOfMonth = form.tpDayOfMonth;
    }
    const input = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      enabled: form.enabled,
      trigger: form.trigger,
      triggerParams,
      conditions,
      action: form.action,
      actionParams,
    };
    if (isEdit) {
      onUpdate(a!.id, input);
    } else {
      onCreate(input as CreateAutomationInput);
    }
  }

  return (
    <ModalShell title={isEdit ? 'Edit automation' : 'New automation'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Name</label>
          <input
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            maxLength={120}
            required
          />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Description</label>
          <input
            value={form.description}
            onChange={(e) => updateForm('description', e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            maxLength={500}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">When (trigger)</label>
            <select
              value={form.trigger}
              onChange={(e) => updateForm('trigger', e.target.value as AutomationOutput['trigger'])}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            >
              {Object.entries(TRIGGER_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Then (action)</label>
            <select
              value={form.action}
              onChange={(e) => updateForm('action', e.target.value as AutomationOutput['action'])}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
            >
              {Object.entries(ACTION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        </div>

        {isDueN && (
          <div>
            <label className="block text-xs text-muted mb-1">Days (1–90)</label>
            <input
              type="number"
              min={1}
              max={90}
              value={form.tpDays}
              onChange={(e) => updateForm('tpDays', Number(e.target.value))}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
              required
            />
            <p className="text-xs text-muted mt-1">
              {form.trigger === 'ITEM_DUE_IN_N_DAYS'
                ? 'Fires when an item’s deadline falls within the next N days.'
                : 'Fires when an item’s deadline passed more than N days ago.'}
            </p>
          </div>
        )}
        {form.trigger === 'SCHEDULE' && (
          <div className="bg-bg-card border border-border rounded-lg p-3 space-y-3">
            <div className="text-xs font-medium text-muted">Schedule (PKT) — sweeps matching non-completed items once per scheduled day</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-muted mb-1">Every</label>
                <select
                  value={form.tpFrequency}
                  onChange={(e) => updateForm('tpFrequency', e.target.value as 'daily' | 'weekly' | 'monthly')}
                  className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                >
                  <option value="daily">Day</option>
                  <option value="weekly">Week</option>
                  <option value="monthly">Month</option>
                </select>
              </div>
              {form.tpFrequency === 'weekly' && (
                <div>
                  <label className="block text-xs text-muted mb-1">On</label>
                  <select
                    value={form.tpDayOfWeek}
                    onChange={(e) => updateForm('tpDayOfWeek', Number(e.target.value))}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                  >
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                    <option value={0}>Sunday</option>
                  </select>
                </div>
              )}
              {form.tpFrequency === 'monthly' && (
                <div>
                  <label className="block text-xs text-muted mb-1">Day of month</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.tpDayOfMonth}
                    onChange={(e) => updateForm('tpDayOfMonth', Number(e.target.value))}
                    className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-muted mb-1">Hour (0–23)</label>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={form.tpHour}
                  onChange={(e) => updateForm('tpHour', Number(e.target.value))}
                  className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Minute</label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={form.tpMinute}
                  onChange={(e) => updateForm('tpMinute', Number(e.target.value))}
                  className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                  required
                />
              </div>
            </div>
          </div>
        )}

        <div className="bg-bg-card border border-border rounded-lg p-3 space-y-3">
          <div className="text-xs font-medium text-muted">Conditions (all optional — leave blank to match everything)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Jurisdiction</label>
              <select
                value={form.condJurisdiction}
                onChange={(e) => updateForm('condJurisdiction', e.target.value)}
                className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="">Any</option>
                {JURISDICTIONS.map((j) => <option key={j.code} value={j.code}>{j.code}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Service type</label>
              <input
                value={form.condServiceType}
                onChange={(e) => updateForm('condServiceType', e.target.value)}
                className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                placeholder="e.g. VAT Return"
              />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Client</label>
              {/* OPS 3.5 #11: server-searched picker (old limit=100 prefetch hid
                  clients past the first page). */}
              <ClientSearchSelect
                value={form.condClientId}
                onChange={(v) => updateForm('condClientId', v)}
                placeholder="Any"
              />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Assignee</label>
              <select
                value={form.condAssigneeId}
                onChange={(e) => updateForm('condAssigneeId', e.target.value)}
                className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="">Any</option>
                {users.data?.filter((u) => u.status !== 'DISABLED').map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
              </select>
            </div>
          </div>
        </div>

        {isNotify && (
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Notification title (optional)</label>
              <input
                value={form.paramTitle}
                onChange={(e) => updateForm('paramTitle', e.target.value)}
                className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                placeholder={form.name || 'Automation name'}
              />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Notification body (optional)</label>
              <textarea
                value={form.paramBody}
                onChange={(e) => updateForm('paramBody', e.target.value)}
                className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white"
                rows={2}
              />
              <p className="text-xs text-muted mt-1">{TEMPLATE_HINT}</p>
            </div>
          </div>
        )}
        {UNIMPLEMENTED_ACTIONS.includes(form.action) && (
          <p className="text-xs text-amber-300">This action type is not executed by the engine yet — the rule can be saved, but firing will be logged and skipped.</p>
        )}

        <label className="flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={form.enabled} onChange={(e) => updateForm('enabled', e.target.checked)} />
          Active
        </label>

        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-xl mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

